import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../App";
import { AlertTriangle, Coffee, Timer, MapPin, X, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GeofenceMonitor() {
  const { user } = useAuth();
  const [geofenceStatus, setGeofenceStatus] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [breakTimer, setBreakTimer] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const intervalRef = useRef(null);
  const breakIntervalRef = useRef(null);
  const dismissedRef = useRef(false);

  const fetchGeofenceStatus = useCallback(async () => {
    if (!user || user.role === "admin") return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API_URL}/api/location/geofence-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setGeofenceStatus(data);

      if (data.on_break && data.break_remaining_seconds > 0) {
        setBreakTimer(data.break_remaining_seconds);
        setShowWarning(false);
        dismissedRef.current = false;
      } else if (data.status === "warning" || (data.minutes_outside >= 15 && !data.inside_geofence && !data.on_break)) {
        if (!dismissedRef.current) setShowWarning(true);
      } else if (data.status === "auto_clocked_out") {
        toast.error("Anda telah di-auto-clock-out kerana berada di luar kawasan kerja terlalu lama.");
        setShowWarning(false);
      } else if (data.inside_geofence) {
        setShowWarning(false);
        dismissedRef.current = false;
      }
    } catch {
      // Silently fail
    }
  }, [user]);

  useEffect(() => {
    if (!user || user.role === "admin") return;
    fetchGeofenceStatus();
    intervalRef.current = setInterval(fetchGeofenceStatus, 30000);
    return () => clearInterval(intervalRef.current);
  }, [user, fetchGeofenceStatus]);

  // Break countdown timer
  useEffect(() => {
    if (breakTimer > 0) {
      breakIntervalRef.current = setInterval(() => {
        setBreakTimer((prev) => {
          if (prev <= 1) {
            clearInterval(breakIntervalRef.current);
            fetchGeofenceStatus();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(breakIntervalRef.current);
    }
  }, [breakTimer, fetchGeofenceStatus]);

  const handleConfirmBreak = async () => {
    setConfirming(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API_URL}/api/location/confirm-break`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        setBreakTimer(data.break_remaining_seconds || 3600);
        setShowWarning(false);
        toast.success("Rehat 1 jam disahkan. Timer bermula.");
      }
    } catch {
      toast.error("Gagal mengesahkan rehat");
    }
    setConfirming(false);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  if (!user || user.role === "admin") return null;
  if (!geofenceStatus) return null;

  // Break timer bar (always visible when on break)
  if (breakTimer > 0) {
    const progress = ((3600 - breakTimer) / 3600) * 100;
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[90vw] max-w-md animate-slide-up" data-testid="break-timer-bar">
        <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-2xl border border-emerald-200 dark:border-emerald-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
              <Coffee className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-stone-800 dark:text-stone-100">Sedang Rehat</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-2 bg-stone-100 dark:bg-stone-700 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all duration-1000" style={{ width: `${progress}%` }} />
                </div>
                <span className="text-sm font-mono font-bold text-emerald-600 dark:text-emerald-400 shrink-0" data-testid="break-countdown">{formatTime(breakTimer)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Warning modal (when outside 15+ min)
  if (!showWarning) return null;

  const minutesOutside = geofenceStatus?.minutes_outside || 0;
  const autoClockOutIn = Math.max(0, 120 - minutesOutside);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in" data-testid="geofence-warning-modal">
      <div className="w-full max-w-md mx-4 mb-4 sm:mb-0 bg-white dark:bg-stone-800 rounded-2xl shadow-2xl border border-red-200 dark:border-red-800 overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="bg-red-50 dark:bg-red-900/20 px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-red-800 dark:text-red-200">Di Luar Kawasan Kerja</h3>
            <p className="text-xs text-red-600 dark:text-red-300">{minutesOutside} minit di luar kawasan</p>
          </div>
          <button onClick={() => { setShowWarning(false); dismissedRef.current = true; }} className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-800/50">
            <X className="w-4 h-4 text-red-400" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
            <Timer className="w-5 h-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Auto clock-out dalam <span className="font-bold">{autoClockOutIn} minit</span> jika tiada tindakan
            </p>
          </div>

          <div className="flex items-start gap-3 p-3 rounded-xl bg-stone-50 dark:bg-stone-900/50">
            <MapPin className="w-5 h-5 text-stone-400 mt-0.5 shrink-0" />
            <p className="text-sm text-stone-600 dark:text-stone-300">
              Anda berada <span className="font-semibold">{geofenceStatus?.distance != null ? `${geofenceStatus.distance}m` : "jarak tidak diketahui"}</span> dari kawasan kerja. Sila kembali atau sahkan rehat.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-2">
            <Button
              onClick={handleConfirmBreak}
              disabled={confirming}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-12 text-base"
              data-testid="confirm-break-btn"
            >
              <Coffee className="w-4 h-4 mr-2" />
              {confirming ? "Mengesahkan..." : "Saya Sedang Rehat (1 Jam)"}
            </Button>
            <Button
              onClick={() => { setShowWarning(false); dismissedRef.current = true; }}
              variant="outline"
              className="w-full rounded-xl h-10 text-sm border-stone-200 dark:border-stone-700"
              data-testid="dismiss-warning-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              Saya Akan Kembali
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
